import Browser from "bowser";
import { imageUrlConfig } from "./imageUrlConfig";
/**
 * @param file
 * @param {*} props
 * @returns {string|undefined}
 */

export default function imageUrl(
  file,
  {
    width,
    rect = "fw",
    origin = false,
    gif = false,
    webp = true,
    domain,
    bucket,
  } = {}
) {
  if (!file || !file.bucket || !file.key) {
    return null;
  }

  let affix = "";
  let sizeMap = {
    fw: [86, 192, 240, 320, 480, 658, 1200],
    sq: [75, 180, 235, 240, 490], // squre 方图
  };

  const sizes = sizeMap[rect];

  if (!sizes) {
    throw Error(`Unsupport rect value: ${rect}`);
  }

  let size = 1200;
  for (let value of sizes) {
    if (value >= width) {
      size = value;
      break;
    }
  }

  affix = `_${rect}`;

  // TODO: 目前配置域名写在用户浏览器本地，会导致SSR和本地渲染不一致报错（待处理） @城管
  const imageConfig = imageUrlConfig.get(file.bucket);
  const realDomain = domain || imageConfig.domain;
  const realBucket = bucket || imageConfig.bucket;

  // 小图场景下，使用small作为图片展示，主要用于素材无水印小图展示
  // 有alias的图片，使用alias路径作为图片展示
  const path = file.alias
    ? "alias/"
    : size <= 480
    ? file.small && "small/"
    : "";

  let url = `https://${realBucket}.${realDomain}/${path || ""}${file.key}${
    origin ? "" : affix + size
  }`;

  // iphone图片尺寸
  const isHeic = file.type === "image/heic";
  if (isHeic) {
    if (size === 658 || size === 1200) {
      url += origin ? "_png" : "png";
    } else if (origin) {
      url += "_png";
    }
  }

  if (gif && (size === 240 || size === 320 || size === 236 || size === 480)) {
    url += "g";
  }

  // 使用webp图片
  if (
    webp &&
    !origin &&
    !isHeic &&
    !gif &&
    isSupportWebp() &&
    [86, 192, 240, 480, 75, 180, 490, 658, 1200].includes(size)
  ) {
    // webp图片格式支持最大图片单边尺寸限制 16383px
    const isValidSize =
      (file.height &&
        file.width &&
        (file.height < 16380 || (file.height * size) / file.width < 16380)) ||
      false;

    // 注意：修改尺寸需要OSS配置 📢
    if (isValidSize && file.type !== "image/gif") {
      // gif图片使用png图片，不添加webp，因为webp格式默认是动图
      url += "webp";
    }
  } else if (
    ["image/jpeg", "image/jpg"].includes(file.type) &&
    [75, 86, 180, 192, 240, 480, 490].includes(size)
  ) {
    // 原图片个格式是jpeg且指定尺寸的图片使用jpeg格式
    url += "jpeg";
  }

  return url;
}

export function getErrorPageImage(file, { width } = {}) {
  if (!file || !file.bucket || !file.key) {
    return null;
  }
  let affix = "";
  if (width === 82.5) {
    // 详情页画板小图
    affix = `_fw86`;
  } else if (width) {
    affix = `_fw${width}`;
  } else {
    affix = `_fw240`;
  }

  if (file.bucket.indexOf("gd-") !== 0) {
    file.bucket = "gd-" + file.bucket;
  }

  return `https://${file.bucket}.huaban.com/img/error_page/${file.key}${affix}`;
}

export function getAuthorizePageImage(file, { width } = {}) {
  if (!file || !file.bucket || !file.key) {
    return null;
  }
  let affix = "";
  if (width === 28) {
    // 详情页画板小图
    affix = `_fw86`;
  } else if (width) {
    affix = `_fw${width}`;
  } else {
    affix = `_fw240`;
  }

  if (file.bucket.indexOf("gd-") !== 0) {
    file.bucket = "gd-" + file.bucket;
  }

  return `https://${file.bucket}.huaban.com/img/apps/${file.key}${affix}`;
}

/**
 * 获取 oss 图片地址
 * @param {string} bucket
 * @param {string} key
 * @param {Record} config
 */
export function getOSSImage(bucket, key, config) {
  if (bucket.indexOf("gd-") !== 0) {
    bucket = "gd-" + bucket;
  }

  let configStr = "";
  for (const [func, param] of Object.entries(config ?? {})) {
    if (
      param === undefined ||
      param === null ||
      (typeof param === "number" && isNaN(param))
    ) {
      continue;
    }
    configStr += `/${func}/${param}`;
  }
  return `https://${bucket}.huaban.com/${key}_${configStr}`;
}

/**
 * 获取webp格式图片
 * @param {string} bucket
 * @param {string} key
 * @param {Record} config
 */
export function getWebp(bucket, key, config) {
  return getOSSImage(bucket, key, { ...config, format: "webp" });
}

/**
 * 获取指定尺寸的替身图片，按照最小边缩放并居中裁剪
 * @param {string} bucket
 * @param {string} key
 * @param {number} width
 * @param {number=} height 默认和width一致
 */
export function getAvatar(bucket, key, width, height) {
  return getWebp(bucket, key, {
    both: `${width}x${height ?? width}`,
  });
}

/**
 * 检测浏览器是否支持webp格式的图片
 */
export function isSupportWebp(userAgent) {
  if (isSupportWebp.supportValue === undefined) {
    let result = false;

    try {
      if (userAgent) {
        result = isSupportWebpByUa(userAgent);
      } else {
        const elem = document.createElement("canvas");
        if (elem.getContext && elem.getContext("2d")) {
          // was able or not to get WebP representation
          result = elem.toDataURL("image/webp").indexOf("data:image/webp") == 0;
        } else {
          // very old browser like IE 8, canvas not supported
          result = false;
        }
      }
    } catch (e) {
      result = false;
    }

    isSupportWebp.supportValue = result;
  }
  return isSupportWebp.supportValue;
}

// 浏览器webp兼容性检查
const isSupportWebpByUa = (userAgent) => {
  if (!userAgent) {
  } else {
    const ua = userAgent?.toLowerCase();
    const browser = Browser.getParser(ua);

    // IE 10 = Trident/6.0
    // IE 11 = Trident/7.0
    if (browser.getEngineName() === "Trident") {
      return false;
    } else {
      // 版本适配参考： https://caniuse.com/webp
      // 所有浏览器名称地址: https://github.com/lancedikson/bowser/blob/master/src/constants.js
      const notSupport = browser.satisfies({
        chrome: "<32",
        safari: "<16.0",
        opera: "<19",
        edge: "<18", // edge 16 开始使用chromium 58内核
        firefox: "<65",
        qq: "<13.1",

        mobile: {
          safari: "<14",
          android: "<4.2",
          chrome: "<107",
          samsung_internet: "<4",
          qq: "<13.1",
          firefox: "<106",
          uc: "<13.4",
        },
      });

      return !notSupport;
    }
  }
};

export function gdOssUrl(url, options = {}) {
  // 其他域名不处理
  if (
    !/dancf\.com/.test(url) &&
    !/grocery-cdn/.test(url) &&
    !/gd-hb/.test(url)
  ) {
    return url;
  }

  // 避免重复处理
  if (/\?x-oss-process=/.test(url)) return url;
  const {
    width,
    height,
    type = "webp",
    scaleType,
    minDpr = 1.5,
    useDpr = true,
    resizeType = 0,
    sharpen = 110,
    crop,
  } = options;
  let src = url + (url.includes("?") ? "&" : "?") + "x-oss-process=";
  if ((width || height) && !/\.gif$/.test(url)) {
    src += "image/resize";

    // 最大限制 2， 防止图片过大， 最小1.5 保证图片清晰
    const dpr = useDpr ? Math.min(2, minDpr) : 1;

    if (width) {
      src += `,w_${Math.round(width * dpr)}`;
    }
    if (height) {
      src += `,h_${Math.round(height * dpr)}`;
    }
    if (scaleType) {
      // https://help.aliyun.com/document_detail/44688.html?spm=a2c4g.11186623.6.1383.36e335a8pxsRMg
      src += `,m_${scaleType}`;
    }
    if (resizeType === 6) {
      src += ",type_6";
    }
    if (crop) {
      src += "/crop";
      if (crop.width) {
        src += `,w_${Math.round(crop.width)}`;
      }
      if (crop.height) {
        src += `,h_${Math.round(crop.height)}`;
      }
    }
    if (sharpen) {
      src += `/sharpen,${sharpen}`;
    }
    src += "/interlace,1,";
  }

  if (type && !/\.svg$/.test(url)) {
    if (type === "webp" || /\.webp$/.test(url)) {
      if (isSupportWebp()) {
        src += `image/format,${type},`;
      } else if (options.allowJPGOnUnsupportedWebp && !/\.gif$/.test(url)) {
        src += "image/format,jpg,";
      }
    } else {
      src += `image/format,${type},`;
    }
  }
  if (/\?x-oss-process=$/.test(src)) return url;
  return src.replace(/,$/, "");
}
/**
 * 图片展示src优化策略：
 * @param url  预览图片地址
 * @param {Partial< {
 *  displayWidth: number,
 *  displayHeight: number,
 *  width: number,
 *  height: number,
 *  aspectRatio: number,
 *  sharpen: number,
 *  type: string
 * }>} options 其他配置
 {
     displayWidth: 图片展示宽度
     Width: 图片的实际宽度尺寸
     height：预览图片的高度尺寸
     aspectRatio: 要显示的高宽比
 }
 */
export function gdImgSrcPolicy(url, options = {}) {
  if (url?.includes(".svg")) {
    return {
      src: url,
    };
  }

  const {
    displayWidth,
    width = 1600,
    height,
    aspectRatio,
    sharpen = 80,
    ...rest
  } = options;

  const ossOptions = {
    minDpr: 1,
    sharpen,
    ...rest,
  };

  // 按高度展示
  if (options.displayHeight) {
    // 检查处理后宽度是否会存在没有容器宽的情况
    if (width && height && aspectRatio) {
      const displayHeight = options.displayHeight;
      let previewAspectRatio = height / width;
      // 转换后展示的宽度
      let tranformWidth = displayHeight / previewAspectRatio;
      // 实际容器的宽度
      let displayWidth = displayHeight / aspectRatio;

      // 宽度不够，用实际容器宽度来做oss处理
      if (displayWidth > tranformWidth) {
        return {
          src: gdOssUrl(url, {
            width: displayWidth,
            ...ossOptions,
          }),
          srcSet:
            gdOssUrl(url, {
              width: displayWidth,
              ...ossOptions,
              minDpr: 2,
            }) + " 2x",
        };
      }
    }

    return {
      src: gdOssUrl(url, {
        height: options.displayHeight,
        minDpr: 1,
        ...ossOptions,
      }),
      srcSet:
        gdOssUrl(url, {
          height: options.displayHeight,
          ...ossOptions,
          minDpr: 2,
        }) + " 2x",
    };
  }

  let src = gdOssUrl(url, {
    width: displayWidth,
    ...ossOptions,
  });

  let srcset =
    gdOssUrl(url, {
      width: displayWidth,
      ...ossOptions,
      minDpr: 2,
    }) + " 2x";

  // 检查高度是否会存在没有容器高的情况
  if (width && height && aspectRatio) {
    let previewAspectRatio = height / width;
    // 转换后展示的高度
    let tranformHeight = displayWidth * previewAspectRatio;
    // 实际容器的高度
    let displayHeight = displayWidth * aspectRatio;

    // 如果转换后的高度填不满要显示的高度，则用实际容器展示高度来做oss处理
    if (displayHeight > tranformHeight) {
      src = gdOssUrl(url, {
        height: displayHeight,
        ...ossOptions,
      });

      srcset =
        gdOssUrl(url, {
          height: displayHeight,
          ...ossOptions,
          minDpr: 2,
        }) + " 2x";
    }
  }

  return {
    src,
    srcSet: srcset,
  };
}

// 最好在客户端渲染使用，服务端只返回1倍屏
// 获取合适分辨率倍数的图片
/**
 *
 * @param {string} url
 * @param {{
 *  width?: number,
 *  height?: number,
 *  sharpen?: number,
 *  type?: string
 * }} option
 * @returns
 */
export function getAdaptiveImg(url, option) {
  if (typeof window === "undefined") {
    return gdOssUrl(url, {
      minDpr: 1,
      ...option,
    });
  }
  return gdOssUrl(url, {
    minDpr: window.devicePixelRatio,
    ...option,
  });
}

export function getImageSrc2x(...args) {
  const [file, options] = args;
  return {
    src: imageUrl(...args),
    srcSet:
      imageUrl(file, {
        ...options,
        ...(options && options.width ? { width: options.width * 2 } : null),
      }) + " 2x",
  };
}
